import { makeStyles } from '@material-ui/core';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import venturesAPI, {
  VentureNote as VentureNoteType,
} from '../../api/ventures';
import {
  NewNote,
  Note,
  PageLoader,
  SnackMessage,
} from '../../components/common';
import { NoteType } from '../../contexts/personal-notes-context';
import { VentureContext } from '../../contexts/venture-context';
import { useNoteScroll } from '../../hooks/useNoteScroll';
import { StringDate } from '../../utils/date/StringDate';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    alignItems: 'center',
  },
});

function VentureNotesPage() {
  const classes = useStyles();
  const { ventureId } = useParams<{ ventureId: string }>();
  const { venture } = useContext(VentureContext);
  const [updatingNotesIds, setUpdaingNotesIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [deletingNotesIds, setDeletingNotesIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [isCreating, setIsCreating] = useState(false);
  const [notes, setNotes] = useState<VentureNoteType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeNoteForm, setActiveNoteForm] = useState<boolean>(false);
  const noteType = NoteType.VentureNote;

  const { enqueueSnackbar } = useSnackbar();

  // Use the custom hook for note scrolling
  useNoteScroll({ notes, isLoading });

  const handleCreateNote = useCallback(
    async (
      note: {
        contents: string;
        attachmentRefs: string;
        creationDate: StringDate;
      },
      callback,
    ) => {
      try {
        setIsCreating(true);
        if (venture) {
          const createdNotes = (await venturesAPI.createNote({
            ventureId: venture.id,
            contents: note.contents,
            attachmentRefs: note.attachmentRefs,
            creationDate: note.creationDate,
          })) as VentureNoteType;
          setNotes((prev) => [createdNotes, ...prev]);
        }

        setIsCreating(false);
        callback();

        enqueueSnackbar('The note was successfully added.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while creating the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setIsCreating(false);
      }
    },
    [venture, enqueueSnackbar],
  );

  const handleNoteUpdate = useCallback(
    async (note: VentureNoteType, callback) => {
      try {
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: true }));
        if (venture) {
          await venturesAPI.updateNote(note);
          setNotes((prev) =>
            prev.map((ventureNotes) =>
              ventureNotes.id === note.id ? note : ventureNotes,
            ),
          );
          setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: false }));
        }

        callback();
        enqueueSnackbar('The note was successfully updated.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while updating the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setUpdaingNotesIds((prev) => ({ ...prev, [note.id]: false }));
      }
    },
    [venture, enqueueSnackbar],
  );

  const handleUploadFile = useCallback(
    async (file: File) => {
      try {
        const loadedFileURL = await venturesAPI.attachFileToNote(
          ventureId,
          file,
        );
        return loadedFileURL;
      } catch (e: any) {
        return '';
      }
    },
    [ventureId],
  );

  const handleNoteRemove = useCallback(
    async (note: VentureNoteType, callback: () => any) => {
      try {
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: true }));
        if (venture) {
          await venturesAPI.removeNote(venture.id, note.id);
          setNotes((prev) =>
            prev.filter((ventureNote) => ventureNote.id !== note.id),
          );
        }

        callback();
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: false }));
        enqueueSnackbar('The note was successfully removed.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        enqueueSnackbar(
          'An error occurred while removing the note. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        setDeletingNotesIds((prev) => ({ ...prev, [note.id]: false }));
      }
    },
    [venture, enqueueSnackbar],
  );

  const loadNotes = async (ventureId: string) => {
    try {
      setIsLoading(true);
      const responseNotes = await venturesAPI.getNotes(ventureId);

      const sortNotes = responseNotes.sort((a, b) => {
        const prevDate = +new Date(a.creationDate);
        const nextDate = +new Date(b.creationDate);
        return nextDate - prevDate;
      });

      setNotes(sortNotes);
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar(
        (error as AxiosError<{ message: string }>).response?.data.message,
        {
          variant: 'error',
        },
      );

      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotes(ventureId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventureId]);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <NewNote
        creating={isCreating}
        disabled={activeNoteForm}
        noteType={noteType}
        onUploadFile={handleUploadFile}
        onCreate={handleCreateNote}
        onCreateModeChange={setActiveNoteForm}
      />
      <div>
        {notes.map((note, index) => (
          <Note<VentureNoteType>
            key={note.id}
            note={note}
            updating={updatingNotesIds[note.id]}
            deleting={deletingNotesIds[note.id]}
            disabledEdit={activeNoteForm}
            onEdit={handleNoteUpdate}
            onUploadFile={handleUploadFile}
            onRemove={handleNoteRemove}
            onEditModeChange={setActiveNoteForm}
            noteType={noteType}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

export default VentureNotesPage;
